(function () {
  'use strict';

  const ACTIVE_QUERY = '(prefers-reduced-motion: no-preference)';
  const SELECTORS = {
    hero: '[data-journey-hero]',
    carousel: '[data-journey-carousel]',
    runway: '[data-journey-runway]',
    sourceShell: '[data-journey-source-shell]',
    sourceFrame: '[data-journey-source-frame]',
    sourceVisual: '[data-journey-source-visual]',
    proxySeed: '[data-journey-proxy-seed]',
    frameImage: '[data-journey-frame-image]',
    track: '.sb-carousel__track',
    liveCard: '[data-journey-live-card]',
    liveVisual: '[data-journey-live-visual]',
  };

  const STATE = {
    swapAtDesktop: 0.015,
    swapAtMobile: 0.001,
    handoffAtDesktop: 0.04,
    handoffAtMobile: 0.06,
    midpointDesktop: 0.74,
    midpointMobile: 0.68,
    landAt: 0.992,
    scaleShiftAt: 0.82,
    frameCycleStartDesktop: 0.09,
    frameCycleStartMobile: 0.12,
    frameCycleEnd: 0.94,
    destinationViewportYDesktop: 0.58,
    destinationViewportYMobile: 0.54,
  };

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function interpolate(start, end, progress) {
    return start + (end - start) * progress;
  }

  function mapProgress(value, start, end) {
    if (end <= start) return value >= end ? 1 : 0;
    return clamp((value - start) / (end - start), 0, 1);
  }

  function debounce(fn, wait) {
    let timeoutId = null;

    return function debounced() {
      const context = this;
      const args = arguments;

      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(function run() {
        fn.apply(context, args);
      }, wait);
    };
  }

  function preloadImageSource(source) {
    return new Promise(function preloadPromise(resolve) {
      if (!source) {
        resolve();
        return;
      }

      let settled = false;
      const image = new window.Image();

      function finish() {
        if (settled) return;
        settled = true;
        resolve();
      }

      image.decoding = 'async';
      image.onload = finish;
      image.onerror = finish;
      image.src = source;

      if (image.complete) {
        finish();
        return;
      }

      if (typeof image.decode === 'function') {
        image.decode().then(finish).catch(function ignoreDecodeError() {});
      }
    });
  }

  function getNextCarousel(hero, carousels, usedCarousels) {
    for (let index = 0; index < carousels.length; index += 1) {
      const carousel = carousels[index];
      if (usedCarousels.has(carousel)) continue;

      const relationship = hero.compareDocumentPosition(carousel);
      if (relationship & Node.DOCUMENT_POSITION_FOLLOWING) {
        usedCarousels.add(carousel);
        return carousel;
      }
    }

    return null;
  }

  function findPairs() {
    const heroes = Array.from(document.querySelectorAll(SELECTORS.hero));
    const carousels = Array.from(document.querySelectorAll(SELECTORS.carousel)).filter(function filterCarousel(carousel) {
      return carousel.querySelector(SELECTORS.liveCard) && carousel.querySelector(SELECTORS.liveVisual);
    });
    const usedCarousels = new Set();

    return heroes
      .map(function createPair(hero) {
        const carousel = getNextCarousel(hero, carousels, usedCarousels);
        if (!carousel) return null;
        return { hero: hero, carousel: carousel };
      })
      .filter(Boolean);
  }

  function JourneyPair(nodes, index) {
    this.index = index;
    this.hero = nodes.hero;
    this.carousel = nodes.carousel;
    this.runway = this.hero.querySelector(SELECTORS.runway);
    this.sourceShell = this.hero.querySelector(SELECTORS.sourceShell);
    this.sourceFrame = this.hero.querySelector(SELECTORS.sourceFrame);
    this.sourceVisual = this.hero.querySelector(SELECTORS.sourceVisual);
    this.proxySeed = this.hero.querySelector(SELECTORS.proxySeed);
    this.frameImages = Array.from(this.hero.querySelectorAll(SELECTORS.frameImage));
    this.track = this.carousel.querySelector(SELECTORS.track);
    this.liveCard = this.carousel.querySelector(SELECTORS.liveCard);
    this.liveVisual = this.carousel.querySelector(SELECTORS.liveVisual);

    this.proxy = null;
    this.proxyFrameLayers = [];
    this.trigger = null;
    this.matchMedia = null;
    this.resizeObserver = null;
    this.cleanupCallbacks = [];
    this.requestRefresh = debounce(this.refresh.bind(this), 120);
    this.metrics = null;
    this.frameSources = [];
    this.frameSequence = [];
    this.currentFrameIndex = -1;
    this.frameAssetsPromise = null;
    this.frameAssetsReady = false;
    this.enableNonce = 0;
    this.active = false;
    this.staticFallback = false;
    this.topProgressOffset = 0;
    this.wasSourceVisible = true;
    this.trackInteractionPrimed = false;
    this.suppressTrackInteraction = false;
    this.lastTrackScrollLeft = this.track ? this.track.scrollLeft : 0;
    this.runwayCollapseDeferred = false;
  }

  JourneyPair.prototype.isValid = function isValid() {
    return Boolean(
        this.runway &&
        this.sourceShell &&
        this.sourceFrame &&
        this.sourceVisual &&
        this.proxySeed &&
        this.liveCard &&
        this.liveVisual
    );
  };

  JourneyPair.prototype.mount = function mount() {
    if (!this.isValid()) return;

    this.frameSources = this.getJourneyFrameSources();
    this.frameSequence = this.getJourneyFrameSequence();
    this.primeFrameAssets();

    this.matchMedia = window.gsap.matchMedia();
    this.matchMedia.add(ACTIVE_QUERY, this.enable.bind(this));
  };

  JourneyPair.prototype.enable = function enable() {
    if (this.active) return undefined;

    this.active = true;
    this.staticFallback = false;
    this.wasSourceVisible = true;
    this.trackInteractionPrimed = false;
    this.suppressTrackInteraction = false;
    this.lastTrackScrollLeft = this.track ? this.track.scrollLeft : 0;
    this.runwayCollapseDeferred = false;
    this.frameSources = this.getJourneyFrameSources();
    this.frameSequence = this.getJourneyFrameSequence();
    this.currentFrameIndex = -1;
    this.frameAssetsReady = false;
    this.hero.classList.add('sb-hero--journey-ready');
    this.carousel.classList.add('sb-carousel--journey-ready');
    this.carousel.classList.remove('sb-carousel--journey-complete');
    this.liveCard.classList.remove('journey-complete');

    if (this.shouldUseStaticFallback()) {
      this.activateStaticFallback();
      return this.disable.bind(this);
    }

    this.enableNonce += 1;
    const enableNonce = this.enableNonce;
    this.primeFrameAssets().then(
      function finishEnableAfterAssets() {
        if (!this.active || this.staticFallback || this.enableNonce !== enableNonce) return;
        this.finishEnable();
      }.bind(this)
    );

    return this.disable.bind(this);
  };

  JourneyPair.prototype.finishEnable = function finishEnable() {
    if (!this.active || this.staticFallback || this.proxy || this.trigger) return;

    this.proxy = this.createProxy();
    this.bindObservers();
    this.centerLiveCard();

    this.trigger = window.ScrollTrigger.create({
      id: 'sb-product-journey-' + this.index,
      trigger: this.runway,
      start: this.getJourneyStart.bind(this),
      endTrigger: this.liveVisual,
      end: this.getJourneyEnd.bind(this),
      scrub: true,
      invalidateOnRefresh: true,
      onRefresh: this.handleRefresh.bind(this),
      onUpdate: this.handleUpdate.bind(this),
    });

    window.ScrollTrigger.refresh();
  };

  JourneyPair.prototype.disable = function disable() {
    this.active = false;
    this.teardownAnimatedJourney();
    this.staticFallback = false;
    this.wasSourceVisible = true;
    this.trackInteractionPrimed = false;
    this.suppressTrackInteraction = false;
    this.lastTrackScrollLeft = this.track ? this.track.scrollLeft : 0;
    this.runwayCollapseDeferred = false;
    this.frameSources = [];
    this.frameSequence = [];
    this.currentFrameIndex = -1;
    this.frameAssetsPromise = null;
    this.frameAssetsReady = false;
    this.enableNonce += 1;
    this.topProgressOffset = 0;
    this.hero.classList.remove('sb-hero--journey-ready', 'sb-hero--journey-active');
    this.hero.classList.remove('sb-hero--journey-fallback');
    this.carousel.classList.remove('sb-carousel--journey-ready', 'sb-carousel--journey-complete');
    this.liveCard.classList.remove('journey-complete');

    window.gsap.set(this.sourceVisual, { clearProps: 'opacity,pointerEvents' });
  };

  JourneyPair.prototype.teardownAnimatedJourney = function teardownAnimatedJourney() {
    if (this.trigger) {
      this.trigger.kill();
      this.trigger = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    while (this.cleanupCallbacks.length) {
      const callback = this.cleanupCallbacks.pop();
      callback();
    }

    if (this.proxy) {
      this.proxy.remove();
      this.proxy = null;
    }

    this.proxyFrameLayers = [];
    this.metrics = null;
  };

  JourneyPair.prototype.destroy = function destroy() {
    if (this.matchMedia) {
      this.matchMedia.revert();
      this.matchMedia = null;
    }

    this.disable();
  };

  JourneyPair.prototype.createProxy = function createProxy() {
    const proxy = document.createElement('div');
    proxy.className = 'sb-journey-proxy';
    const frameSources = this.frameSources.length ? this.frameSources : this.getJourneyFrameSources();
    const sourceNodes = [this.proxySeed].concat(this.frameImages);

    this.proxyFrameLayers = frameSources.map(
      function createProxyFrame(source, index) {
        const baseNode = sourceNodes[index] || this.proxySeed;
        const clone = baseNode.cloneNode(true);
        clone.removeAttribute('data-journey-proxy-seed');
        clone.removeAttribute('data-journey-frame-image');
        clone.removeAttribute('loading');
        clone.removeAttribute('fetchpriority');
        clone.setAttribute('decoding', 'async');
        clone.classList.add('sb-journey-proxy-frame');

        if (source) {
          clone.setAttribute('src', source);
          clone.removeAttribute('srcset');
          clone.removeAttribute('sizes');
        }

        clone.style.opacity = index === 0 ? '1' : '0';
        proxy.appendChild(clone);
        return clone;
      }.bind(this)
    );

    this.currentFrameIndex = 0;
    document.body.appendChild(proxy);
    return proxy;
  };

  JourneyPair.prototype.bindObservers = function bindObservers() {
    const onResize = this.requestRefresh;
    window.addEventListener('resize', onResize, { passive: true });
    this.cleanupCallbacks.push(function cleanupResize() {
      window.removeEventListener('resize', onResize);
    });

    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(this.requestRefresh);
      [this.sourceShell, this.sourceFrame, this.liveCard, this.liveVisual, this.track].forEach(function observeNode(node) {
        if (node) observer.observe(node);
      });
      this.resizeObserver = observer;
    }

    [this.proxySeed].concat(this.frameImages).forEach(
      function bindImageRefresh(image) {
        if (!image || image.complete) return;

        const refresh = this.requestRefresh;
        image.addEventListener('load', refresh);
        image.addEventListener('error', refresh);

        this.cleanupCallbacks.push(function cleanupImageListener() {
          image.removeEventListener('load', refresh);
          image.removeEventListener('error', refresh);
        });
      }.bind(this)
    );

    this.bindCarouselInteractions();
  };

  JourneyPair.prototype.refresh = function refresh() {
    if (!this.active || !this.trigger || !window.ScrollTrigger) return;
    window.ScrollTrigger.refresh();
  };

  JourneyPair.prototype.primeFrameAssets = function primeFrameAssets() {
    if (this.frameAssetsPromise) return this.frameAssetsPromise;

    const sources = Array.from(new Set((this.frameSources.length ? this.frameSources : this.getJourneyFrameSources()).filter(Boolean)));

    this.frameAssetsPromise = Promise.all(sources.map(preloadImageSource)).then(
      function markFrameAssetsReady() {
        this.frameAssetsReady = true;
      }.bind(this)
    );

    return this.frameAssetsPromise;
  };

  JourneyPair.prototype.getJourneyFrameSources = function getJourneyFrameSources() {
    const fallbackSource = this.proxySeed ? (this.proxySeed.getAttribute('src') || this.proxySeed.currentSrc) : '';
    const frameSources = [fallbackSource];

    this.frameImages.forEach(function collectFrameSource(image) {
      const source = image.getAttribute('src') || image.currentSrc || fallbackSource;
      frameSources.push(source || fallbackSource);
    });

    while (frameSources.length < 5) {
      frameSources.push(fallbackSource);
    }

    return frameSources.slice(0, 5);
  };

  JourneyPair.prototype.getJourneyFrameSequence = function getJourneyFrameSequence() {
    return [0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 0, 0];
  };

  JourneyPair.prototype.getAnimatedFrameIndex = function getAnimatedFrameIndex(progress) {
    const sequence = this.frameSequence.length ? this.frameSequence : this.getJourneyFrameSequence();
    if (!sequence.length) return 0;

    const frameCycleStart = this.isMobile() ? STATE.frameCycleStartMobile : STATE.frameCycleStartDesktop;
    const frameProgress = mapProgress(progress, frameCycleStart, STATE.frameCycleEnd);
    const frameSlot = Math.min(sequence.length - 1, Math.floor(frameProgress * sequence.length));
    return sequence[frameSlot];
  };

  JourneyPair.prototype.setActiveProxyFrame = function setActiveProxyFrame(frameIndex) {
    if (!this.proxyFrameLayers.length) return;

    const nextIndex = clamp(frameIndex, 0, this.proxyFrameLayers.length - 1);
    if (this.currentFrameIndex === nextIndex) return;

    this.proxyFrameLayers.forEach(function setFrameOpacity(layer, index) {
      layer.style.opacity = index === nextIndex ? '1' : '0';
    });

    this.currentFrameIndex = nextIndex;
  };

  JourneyPair.prototype.shouldUseStaticFallback = function shouldUseStaticFallback() {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const viewportHeight = window.innerHeight || 0;
    const heroRect = this.hero.getBoundingClientRect();
    const heroTop = heroRect.top + scrollY;
    const fallbackThreshold = heroTop + Math.max(viewportHeight * 0.4, 280);

    return scrollY > fallbackThreshold;
  };

  JourneyPair.prototype.activateStaticFallback = function activateStaticFallback(options) {
    if (this.staticFallback) return;

    const preserveViewport = Boolean(options && options.preserveViewport);

    this.teardownAnimatedJourney();
    this.staticFallback = true;
    this.active = false;
    this.runwayCollapseDeferred = false;
    this.hero.classList.remove('sb-hero--journey-active');
    this.carousel.classList.add('sb-carousel--journey-complete');
    this.liveCard.classList.add('journey-complete');
    window.gsap.set(this.sourceVisual, {
      opacity: 1,
      pointerEvents: '',
    });

    if (preserveViewport) {
      this.deferRunwayCollapse();
    } else {
      this.hero.classList.add('sb-hero--journey-fallback');
    }
  };

  JourneyPair.prototype.deferRunwayCollapse = function deferRunwayCollapse() {
    if (this.runwayCollapseDeferred) return;

    this.runwayCollapseDeferred = true;

    const maybeCollapseRunway = function maybeCollapseRunway() {
      if (!this.runwayCollapseDeferred) return;

      const carouselTop = this.carousel.getBoundingClientRect().top;
      if (carouselTop < window.innerHeight) return;

      this.runwayCollapseDeferred = false;
      this.hero.classList.add('sb-hero--journey-fallback');
      window.removeEventListener('scroll', maybeCollapseRunway, true);
      window.removeEventListener('resize', maybeCollapseRunway);
    }.bind(this);

    window.addEventListener('scroll', maybeCollapseRunway, { passive: true, capture: true });
    window.addEventListener('resize', maybeCollapseRunway, { passive: true });

    this.cleanupCallbacks.push(function cleanupDeferredCollapse() {
      this.runwayCollapseDeferred = false;
      window.removeEventListener('scroll', maybeCollapseRunway, true);
      window.removeEventListener('resize', maybeCollapseRunway);
    }.bind(this));

    maybeCollapseRunway();
  };

  JourneyPair.prototype.bindCarouselInteractions = function bindCarouselInteractions() {
    const track = this.track;
    if (!track) return;

    const primeTrackInteraction = function primeTrackInteraction() {
      if (this.staticFallback) return;
      this.trackInteractionPrimed = true;
    }.bind(this);

    const handleTrackScroll = function handleTrackScroll() {
      const currentScrollLeft = track.scrollLeft;
      const changed = Math.abs(currentScrollLeft - this.lastTrackScrollLeft) > 1;
      this.lastTrackScrollLeft = currentScrollLeft;

      if (this.suppressTrackInteraction || !changed || !this.trackInteractionPrimed) return;
      this.activateStaticFallback({ preserveViewport: true });
    }.bind(this);

    track.addEventListener('pointerdown', primeTrackInteraction, { passive: true });
    track.addEventListener('touchstart', primeTrackInteraction, { passive: true });
    track.addEventListener('wheel', primeTrackInteraction, { passive: true });
    track.addEventListener('scroll', handleTrackScroll, { passive: true });

    this.cleanupCallbacks.push(function cleanupTrackListeners() {
      track.removeEventListener('pointerdown', primeTrackInteraction);
      track.removeEventListener('touchstart', primeTrackInteraction);
      track.removeEventListener('wheel', primeTrackInteraction);
      track.removeEventListener('scroll', handleTrackScroll);
    });

    Array.from(this.carousel.querySelectorAll('.sb-carousel__arrow')).forEach(
      function bindArrowInteraction(arrow) {
        const handleArrowInteraction = function handleArrowInteraction() {
          this.activateStaticFallback({ preserveViewport: true });
        }.bind(this);

        arrow.addEventListener('click', handleArrowInteraction);
        this.cleanupCallbacks.push(function cleanupArrowInteraction() {
          arrow.removeEventListener('click', handleArrowInteraction);
        });
      }.bind(this)
    );
  };

  JourneyPair.prototype.isMobile = function isMobile() {
    return window.innerWidth < 990;
  };

  JourneyPair.prototype.centerLiveCard = function centerLiveCard() {
    if (!this.isMobile() || !this.track || !this.liveCard) return;

    const targetLeft = this.liveCard.offsetLeft - (this.track.clientWidth - this.liveCard.offsetWidth) / 2;
    const nextScrollLeft = Math.max(0, targetLeft);
    const previousScrollBehavior = this.track.style.scrollBehavior;

    this.suppressTrackInteraction = true;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = nextScrollLeft;
    this.lastTrackScrollLeft = nextScrollLeft;
    this.trackInteractionPrimed = false;

    window.requestAnimationFrame(
      function restoreScrollBehavior() {
        this.track.style.scrollBehavior = previousScrollBehavior;
        this.suppressTrackInteraction = false;
      }.bind(this)
    );
  };

  JourneyPair.prototype.getStartRotation = function getStartRotation() {
    const rotationValue = window.getComputedStyle(this.hero).getPropertyValue('--sb-journey-start-rotation');
    return parseFloat(rotationValue) || 54;
  };

  JourneyPair.prototype.getSwapAt = function getSwapAt() {
    return this.isMobile() ? STATE.swapAtMobile : STATE.swapAtDesktop;
  };

  JourneyPair.prototype.getHandoffAt = function getHandoffAt() {
    return this.isMobile() ? STATE.handoffAtMobile : STATE.handoffAtDesktop;
  };

  JourneyPair.prototype.getJourneyStart = function getJourneyStart() {
    return this.isMobile() ? 'top 132%' : 'top bottom';
  };

  JourneyPair.prototype.getJourneyEnd = function getJourneyEnd() {
    return this.isMobile() ? 'center 54%' : 'center 58%';
  };

  JourneyPair.prototype.measure = function measure(trigger) {
    const sourceFrameRect = this.sourceFrame.getBoundingClientRect();
    const liveVisualRect = this.liveVisual.getBoundingClientRect();
    const sourceWidth = this.sourceFrame.offsetWidth || this.proxySeed.offsetWidth || this.sourceShell.offsetWidth || sourceFrameRect.width || 1;
    const sourceHeight =
      this.sourceFrame.offsetHeight ||
      this.proxySeed.offsetHeight ||
      sourceWidth * ((this.proxySeed.naturalHeight || 1) / (this.proxySeed.naturalWidth || 1));
    const sourceCenterX = sourceFrameRect.left + sourceFrameRect.width / 2;
    const sourceCenterY = sourceFrameRect.top + sourceFrameRect.height / 2;
    const destinationCenterX = liveVisualRect.left + liveVisualRect.width / 2;
    const destinationCenterY =
      window.innerHeight * (this.isMobile() ? STATE.destinationViewportYMobile : STATE.destinationViewportYDesktop);
    const destinationScale = clamp(
      Math.min(liveVisualRect.width / sourceWidth, liveVisualRect.height / sourceHeight),
      0.3,
      1
    );
    const midpointX = interpolate(sourceCenterX, destinationCenterX, this.isMobile() ? 0.48 : 0.54);
    const midpointY = this.isMobile()
      ? clamp(
          interpolate(sourceCenterY, destinationCenterY, 0.34),
          window.innerHeight * 0.42,
          window.innerHeight * 0.56
        )
      : clamp(window.innerHeight * 0.42, window.innerHeight * 0.38, window.innerHeight * 0.46);

    return {
      source: {
        width: sourceWidth,
        height: sourceHeight,
        x: sourceCenterX - sourceWidth / 2,
        y: sourceCenterY - sourceHeight / 2,
      },
      midpoint: {
        x: midpointX - sourceWidth / 2,
        y: midpointY - sourceHeight / 2,
      },
      destination: {
        x: destinationCenterX - sourceWidth / 2,
        y: destinationCenterY - sourceHeight / 2,
        scale: destinationScale,
      },
      startRotation: this.getStartRotation(),
    };
  };

  JourneyPair.prototype.updateTopProgressOffset = function updateTopProgressOffset(trigger) {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    if (scrollY > 6 || !trigger) return;

    this.topProgressOffset = clamp(trigger.progress || 0, 0, 0.18);
  };

  JourneyPair.prototype.render = function render(progress) {
    if (!this.metrics || !this.proxy) return;

    const boundedProgress = clamp(mapProgress(progress, this.topProgressOffset, 1), 0, 1);
    const midpointAt = this.isMobile() ? STATE.midpointMobile : STATE.midpointDesktop;
    const swapAt = this.getSwapAt();
    const handoffAt = this.getHandoffAt();
    const landed = boundedProgress >= STATE.landAt;
    const sourceVisible = boundedProgress < swapAt;
    const handoffProgress = landed ? 1 : mapProgress(boundedProgress, 0, handoffAt);
    const scaleProgress = mapProgress(boundedProgress, STATE.scaleShiftAt, 1);
    const activeFrameIndex = sourceVisible ? 0 : this.getAnimatedFrameIndex(boundedProgress);

    if (!sourceVisible && this.wasSourceVisible && this.trigger) {
      this.metrics = this.measure(this.trigger);
    }

    this.wasSourceVisible = sourceVisible;
    this.setActiveProxyFrame(activeFrameIndex);

    let x = this.metrics.source.x;
    let y = this.metrics.source.y;

    if (boundedProgress <= midpointAt) {
      const localProgress = mapProgress(boundedProgress, 0, midpointAt);
      x = interpolate(this.metrics.source.x, this.metrics.midpoint.x, localProgress);
      y = interpolate(this.metrics.source.y, this.metrics.midpoint.y, localProgress);
    } else {
      const localProgress = mapProgress(boundedProgress, midpointAt, 1);
      x = interpolate(this.metrics.midpoint.x, this.metrics.destination.x, localProgress);
      y = interpolate(this.metrics.midpoint.y, this.metrics.destination.y, localProgress);
    }

    window.gsap.set(this.proxy, {
      width: this.metrics.source.width,
      height: this.metrics.source.height,
      x: x,
      y: y,
      scale: interpolate(1, this.metrics.destination.scale, scaleProgress),
      rotation: interpolate(this.metrics.startRotation, 0, boundedProgress),
      opacity: landed ? 0 : handoffProgress,
    });

    window.gsap.set(this.sourceVisual, {
      opacity: landed ? 0 : 1 - handoffProgress,
      pointerEvents: boundedProgress < handoffAt ? '' : 'none',
    });

    this.hero.classList.toggle('sb-hero--journey-active', boundedProgress > swapAt && !landed);
    this.carousel.classList.toggle('sb-carousel--journey-complete', landed);
    this.liveCard.classList.toggle('journey-complete', landed);
  };

  JourneyPair.prototype.handleRefresh = function handleRefresh(trigger) {
    this.updateTopProgressOffset(trigger);
    this.centerLiveCard();
    this.metrics = this.measure(trigger);
    this.render(trigger.progress);
  };

  JourneyPair.prototype.handleUpdate = function handleUpdate(trigger) {
    if (!this.metrics) {
      this.metrics = this.measure(trigger);
    }

    this.render(trigger.progress);
  };

  const Manager = {
    pairs: [],
    bound: false,
    init: function init() {
      if (!window.gsap || !window.ScrollTrigger) return;

      window.gsap.registerPlugin(window.ScrollTrigger);
      this.destroy();

      this.pairs = findPairs().map(function createPair(nodes, index) {
        const pair = new JourneyPair(nodes, index);
        pair.mount();
        return pair;
      });

      window.requestAnimationFrame(function refreshAfterInit() {
        window.ScrollTrigger.refresh();
      });
    },
    destroy: function destroy() {
      this.pairs.forEach(function destroyPair(pair) {
        pair.destroy();
      });
      this.pairs = [];
    },
    bind: function bind() {
      if (this.bound) return;
      this.bound = true;

      const reinit = function reinit() {
        window.requestAnimationFrame(function runReinit() {
          Manager.init();
        });
      };

      document.addEventListener('shopify:section:load', reinit);
      document.addEventListener('shopify:section:unload', reinit);
      document.addEventListener('shopify:section:reorder', reinit);
      window.addEventListener('load', function refreshOnLoad() {
        if (window.ScrollTrigger) {
          window.ScrollTrigger.refresh();
        }
      });
    },
  };

  function boot() {
    if (window.SmileBoxxProductJourney && typeof window.SmileBoxxProductJourney.destroy === 'function') {
      window.SmileBoxxProductJourney.destroy();
    }

    window.SmileBoxxProductJourney = Manager;
    Manager.bind();
    Manager.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
